/**
 * GA4 event fan-out (client-side)
 * Maps the shared pixel-tracking event names to GA4's event schema and
 * pushes them via gtag when GA is configured. Called from lib/pixel/hooks
 * alongside the Facebook fbq() call — one pipeline, two destinations.
 */

// GA4 item schema: https://developers.google.com/analytics/devguides/collection/ga4/reference/events
interface GA4Item {
  item_id?: string;
  item_name?: string;
  price?: number;
  quantity?: number;
}

function toItems(params: any): GA4Item[] | undefined {
  const items = params?.items;
  if (!Array.isArray(items) || items.length === 0) return undefined;
  return items.slice(0, 50).map((it: any) => ({
    item_id: it?.id ?? it?.product_id ?? undefined,
    item_name: it?.title ?? it?.name ?? it?.product_title ?? undefined,
    price: typeof it?.price === 'number' ? it.price : undefined,
    quantity: typeof it?.quantity === 'number' ? it.quantity : undefined,
  }));
}

function num(value: any): number | undefined {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : undefined;
}

/** Fire a GA4 event if gtag is loaded. Never throws. */
export function trackGA4(eventName: string, params: any): void {
  if (typeof window === 'undefined') return;
  const gtag = (window as any).gtag;
  if (typeof gtag !== 'function') return;

  try {
    const items = toItems(params);
    const value = num(params?.value ?? params?.total ?? params?.amount);
    const currency = params?.currency ?? undefined;

    switch (eventName) {
      case 'PageView':
        return; // collected automatically by gtag config

      case 'ViewContent': {
        gtag('event', 'view_item', {
          currency,
          value,
          items: items ?? [{
            item_id: params?.productId ?? params?.product_id,
            item_name: params?.content_name ?? params?.title,
          }],
        });
        return;
      }

      case 'AddToCart': {
        gtag('event', 'add_to_cart', {
          currency,
          value,
          items: items ?? [{ item_id: params?.productId, item_name: params?.title }],
        });
        return;
      }

      case 'InitiateCheckout': {
        gtag('event', 'begin_checkout', {
          currency,
          value,
          items,
        });
        return;
      }

      case 'Purchase': {
        gtag('event', 'purchase', {
          transaction_id: params?.orderId ?? params?.order_id ?? undefined,
          currency,
          value,
          items,
        });
        return;
      }

      case 'Search': {
        gtag('event', 'search', {
          search_term: params?.search_string ?? params?.searchQuery ?? undefined,
        });
        return;
      }

      default:
        return;
    }
  } catch {
    // GA is non-critical — never break the page
  }
}
